import { parseMultiRange } from "./operations";

/** Per-file page edits made in Merge PDF's "Choose pages" panel. Page numbers are 1-based source pages. */
export interface PageEdits {
  /** Source pages the person turned off. */
  removed?: number[];
  /** Extra clockwise turn per source page (a multiple of 90), added to whatever rotation the page already has. */
  rotations?: Record<number, number>;
}

export type PlannedPage = { index: number; rotate: number };
export type PagePlan = { pages: PlannedPage[] } | { error: string };

/** Normalises a turn to 0, 90, 180 or 270 (negative and over-full turns wrap). */
export function normalizeTurn(deg: number): number {
  return ((Math.round(deg / 90) * 90) % 360 + 360) % 360;
}

export function hasPageEdits(edits?: PageEdits): boolean {
  if (!edits) return false;
  return (edits.removed?.length ?? 0) > 0 || Object.values(edits.rotations ?? {}).some((d) => normalizeTurn(d) !== 0);
}

/**
 * The pages one file contributes, in output order: the typed range first (blank = every page),
 * then pages the person turned off are dropped, and each kept page carries its extra rotation.
 * index is 0-based, ready for pdf-lib. Errors are user-facing.
 */
export function planFilePages(pageCount: number, rangeText: string | undefined, edits?: PageEdits): PagePlan {
  let order: number[] = Array.from({ length: pageCount }, (_, i) => i);
  const text = rangeText?.trim();
  if (text) {
    const r = parseMultiRange(text, pageCount);
    if ("error" in r) return { error: r.error };
    order = r.pages;
  }
  const removed = new Set((edits?.removed ?? []).map((n) => n - 1));
  const pages = order
    .filter((i) => !removed.has(i))
    .map((i) => ({ index: i, rotate: normalizeTurn(edits?.rotations?.[i + 1] ?? 0) }));
  if (pages.length === 0) return { error: "No pages left from this file" };
  return { pages };
}

/** Toggles a source page in or out of a removed list (returns a new array, sorted). */
export function toggleRemoved(removed: number[] | undefined, page: number): number[] {
  const set = new Set(removed ?? []);
  if (set.has(page)) set.delete(page); else set.add(page);
  return Array.from(set).sort((a, b) => a - b);
}

/** Adds a quarter turn (clockwise +90, counter-clockwise -90) to one page; a full circle drops the entry. */
export function turnPage(rotations: Record<number, number> | undefined, page: number, delta: 90 | -90): Record<number, number> {
  const next = { ...(rotations ?? {}) };
  const t = normalizeTurn((next[page] ?? 0) + delta);
  if (t === 0) delete next[page]; else next[page] = t;
  return next;
}

// ---------------- Arranging pages across all files ----------------

/**
 * One page of the merged result, in output order. fileIndex is the position of its file in the list given to the merge;
 * index is the 0-based page in that file; rotate is the extra clockwise turn (0, 90, 180 or 270).
 */
export interface ArrangedPage { fileIndex: number; index: number; rotate: number }

/** Every file's planned pages (range, left-out pages and turns applied), joined in file order: the starting point of the page arranger. */
export function arrangementFromPlans(plans: PagePlan[]): ArrangedPage[] {
  const out: ArrangedPage[] = [];
  plans.forEach((plan, fileIndex) => {
    if ("error" in plan) return;
    for (const p of plan.pages) out.push({ fileIndex, index: p.index, rotate: p.rotate });
  });
  return out;
}

/** Moves the page at `from` to position `to` (new array). */
export function moveArranged(list: readonly ArrangedPage[], from: number, to: number): ArrangedPage[] {
  if (from === to || from < 0 || from >= list.length || to < 0 || to >= list.length) return [...list];
  const next = [...list];
  const [m] = next.splice(from, 1);
  next.splice(to, 0, m);
  return next;
}

/** Adds a quarter turn to the page at `at` (new array); a full circle returns to 0. */
export function turnArranged(list: readonly ArrangedPage[], at: number, delta: 90 | -90): ArrangedPage[] {
  return list.map((p, i) => (i === at ? { ...p, rotate: normalizeTurn(p.rotate + delta) } : p));
}

/** Drops the page at `at` (new array). */
export function removeArranged(list: readonly ArrangedPage[], at: number): ArrangedPage[] {
  return list.filter((_, i) => i !== at);
}
